import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiMessageCircle, FiInstagram, FiFacebook, FiTwitter, FiHeart, FiAward } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = {
    experiences: [
      { name: 'Dawn Chorus Expedition', href: '/experience' },
      { name: 'Night Safari Adventure', href: '/experience' },
      { name: 'Photography Masterclass', href: '/experience' },
      { name: 'Conservation Experience', href: '/experience' }
    ],
    resources: [
      { name: 'Endemic Species Gallery', href: '/gallery' },
      { name: 'Birding Calendar', href: '/birding' },
      { name: 'Expert Guides', href: '/birding' },
      { name: 'Species Checklist', href: '/birding' }
    ],
    lodge: [
      { name: 'Accommodations', href: '/lodge' },
      { name: 'Birder Amenities', href: '/lodge' },
      { name: 'Photography Facilities', href: '/lodge' },
      { name: 'Concierge Services', href: '/lodge' }
    ]
  };

  const socialLinks = [
    { 
      icon: FiInstagram, 
      href: 'https://instagram.com/sinharajabirders',
      label: 'Instagram - Daily sighting photos' 
    },
    { 
      icon: FiFacebook, 
      href: 'https://facebook.com/sinharajabirders',
      label: 'Facebook - Community updates' 
    },
    { 
      icon: FiTwitter, 
      href: 'https://twitter.com/sinharajabirds',
      label: 'Twitter - Real-time sight logs' 
    }
  ];

  const certifications = [
    'UNESCO World Heritage Partner',
    'Sri Lanka Tourism Board Certified',
    'Responsible Tourism Certified',
    'Wildlife Conservation Supporter'
  ];

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-slate-900 to-black">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Lodge Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-1"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">SB</span>
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-white">
                    Sinharaja Birders Lodge
                  </h3>
                  <p className="text-sm text-gray-400">UNESCO Gateway</p>
                </div>
              </div>

              <p className="text-gray-300 mb-6 leading-relaxed">
                The premier birding destination in Sri Lanka's last primary rainforest. 
                Experience 33 endemic species with luxury accommodations and expert guidance.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <a
                  href="tel:+94717714922"
                  className="flex items-center space-x-3 text-gray-300 hover:text-green-400 transition-colors group"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>+94 71 771 4922</span>
                </a>
                
                <a
                  href="mailto:info@sinharajabirders.com"
                  className="flex items-center space-x-3 text-gray-300 hover:text-green-400 transition-colors group"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>info@sinharajabirders.com</span>
                </a>
                
                <div className="flex items-start space-x-3 text-gray-300">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 mt-1 flex-shrink-0" />
                  <span>Weddagala, Sinharaja Forest Reserve, Sri Lanka</span>
                </div>

                <a
                  href="https://wa.me/94717714922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-gray-300 hover:text-green-400 transition-colors group"
                >
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>WhatsApp Direct</span>
                </a>
              </div>

              {/* Social Links */}
              <div className="flex items-center space-x-4 mt-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 glass rounded-full flex items-center justify-center hover:bg-green-400 hover:text-white transition-all duration-300 group"
                    title={social.label}
                  >
                    <SafeIcon 
                      icon={social.icon} 
                      className="w-5 h-5 group-hover:scale-110 transition-transform" 
                    />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Navigation Sections */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Experiences */}
                <div>
                  <h4 className="font-semibold text-white mb-4">Birding Experiences</h4>
                  <ul className="space-y-3">
                    {footerSections.experiences.map((item, index) => (
                      <li key={index}>
                        <Link
                          to={item.href}
                          className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Resources */}
                <div>
                  <h4 className="font-semibold text-white mb-4">Birding Resources</h4>
                  <ul className="space-y-3">
                    {footerSections.resources.map((item, index) => (
                      <li key={index}>
                        <Link
                          to={item.href}
                          className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Lodge */}
                <div>
                  <h4 className="font-semibold text-white mb-4">The Lodge</h4>
                  <ul className="space-y-3">
                    {footerSections.lodge.map((item, index) => (
                      <li key={index}>
                        <Link
                          to={item.href}
                          className="text-gray-400 hover:text-green-400 transition-colors text-sm"
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 pt-8 border-t border-white/20"
          >
            <div className="flex items-center justify-center mb-6">
              <SafeIcon icon={FiAward} className="w-5 h-5 text-yellow-400 mr-2" />
              <h4 className="font-semibold text-white">Certifications & Partnerships</h4>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4">
              {certifications.map((cert, index) => (
                <span
                  key={index}
                  className="bg-white/10 text-gray-300 px-4 py-2 rounded-full text-sm"
                >
                  {cert}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Emergency Contact Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 glass p-6 rounded-2xl border-l-4 border-yellow-400"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h4 className="font-semibold text-white mb-1 flex items-center">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-yellow-400" />
                  24/7 Birding Concierge & Emergency Support
                </h4>
                <p className="text-gray-300 text-sm">
                  Real-time sighting updates, booking assistance, and travel emergency support
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+94717714922"
                  className="btn-primary px-6 py-2 text-sm"
                >
                  Call Now
                </a>
                <a
                  href="https://wa.me/94717714922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass px-6 py-2 text-white font-medium rounded-xl hover:bg-white/20 transition-all duration-300 text-sm text-center"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 bg-black/50">
          <div className="container mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-2 text-gray-400 text-sm mb-4 md:mb-0">
                <span>© 2025 Sinharaja Birders Lodge. All rights reserved.</span>
                <span className="hidden md:inline">•</span>
                <span className="flex items-center space-x-1">
                  <span>Designed and Created by Rajantha</span>
                </span>
              </div>
              
              <div className="flex items-center space-x-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Conservation Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;